import React, { useMemo, useState } from "react";
import { CircularProgress, Skeleton } from "@mui/material";
import * as S from "./styles";
import StatementFilters, { ICustomRange, PeriodKey } from "../statementFilters";
import TransactionItem from "../transactionItem";
import { useTransactions } from "../../hooks/useTransactions";
import { useDebounce } from "../../hooks/useDebounce";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  ITransactionFilters,
  ITransactionItem,
  TransactionType,
} from "../../utils/services/modules/transactions";
import {
  formatDayLabel,
  shiftDay,
  todayInBrasilia,
} from "../../utils/formatDatas";

const EMPTY_RANGE: ICustomRange = { from: "", to: "" };
const ANIMATION_PAGE_SIZE = 20;

const periodToRange = (
  period: PeriodKey,
  customRange: ICustomRange,
): Pick<ITransactionFilters, "from" | "to"> => {
  const today = todayInBrasilia();
  switch (period) {
    case "today":
      return { from: today, to: today };
    case "7d":
      return { from: shiftDay(today, -6), to: today };
    case "30d":
      return { from: shiftDay(today, -29), to: today };
    case "custom":
      return {
        from: customRange.from || undefined,
        to: customRange.to || undefined,
      };
    default:
      return {};
  }
};

interface IStatement {
  token?: string;
  onOpenReceipt: (transaction: ITransactionItem) => void;
}

const Statement: React.FC<IStatement> = ({ token, onOpenReceipt }) => {
  const [type, setType] = useState<TransactionType>("all");
  const [period, setPeriod] = useState<PeriodKey>("all");
  const [customRange, setCustomRange] = useState<ICustomRange>(EMPTY_RANGE);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);

  const filters = useMemo<ITransactionFilters>(
    () => ({
      type,
      search: debouncedSearch || undefined,
      ...periodToRange(period, customRange),
    }),
    [type, period, customRange, debouncedSearch],
  );

  const {
    items,
    total,
    hasMore,
    loading,
    error,
    initialLoading,
    loadMore,
    retry,
  } = useTransactions(token, filters);

  const sentinelRef = useInfiniteScroll(
    loadMore,
    hasMore && !loading && !error && items.length > 0,
  );

  // Agrupa por dia; a lista já vem da mais nova para a mais antiga
  const groups = useMemo(() => {
    const result: { day: string; items: ITransactionItem[] }[] = [];
    items.forEach((item) => {
      const day = item.date.slice(0, 10);
      const last = result[result.length - 1];
      if (last && last.day === day) {
        last.items.push(item);
      } else {
        result.push({ day, items: [item] });
      }
    });
    return result;
  }, [items]);

  const hasActiveFilters =
    type !== "all" || period !== "all" || search.trim() !== "";

  const clearFilters = () => {
    setType("all");
    setPeriod("all");
    setCustomRange(EMPTY_RANGE);
    setSearch("");
  };

  const handlePeriodChange = (
    newPeriod: PeriodKey,
    newRange?: ICustomRange,
  ) => {
    setPeriod(newPeriod);
    if (newRange) setCustomRange(newRange);
  };

  const renderContent = () => {
    if (initialLoading) {
      return (
        <S.List>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={70}
              animation="wave"
              style={{ borderRadius: 12 }}
            />
          ))}
        </S.List>
      );
    }

    if (error && items.length === 0) {
      return (
        <S.Feedback>
          <S.FeedbackText>Não foi possível carregar o extrato.</S.FeedbackText>
          <S.FeedbackButton type="button" onClick={retry}>
            Tentar novamente
          </S.FeedbackButton>
        </S.Feedback>
      );
    }

    if (items.length === 0) {
      return (
        <S.Feedback>
          <S.EmptyIcon />
          <S.FeedbackText>
            {hasActiveFilters
              ? "Nenhuma transação encontrada para esses filtros."
              : "Nenhuma transação ainda."}
          </S.FeedbackText>
          {hasActiveFilters && (
            <S.FeedbackButton type="button" onClick={clearFilters}>
              Limpar filtros
            </S.FeedbackButton>
          )}
        </S.Feedback>
      );
    }

    let position = 0;

    return (
      <>
        <S.Summary>
          {total} {total === 1 ? "transação" : "transações"}
        </S.Summary>

        {groups.map((group) => (
          <S.Group key={group.day}>
            <S.DayLabel>{formatDayLabel(group.day)}</S.DayLabel>
            <S.List>
              {group.items.map((item) => (
                <TransactionItem
                  key={item.id}
                  transaction={item}
                  // cada página nova anima a partir do início
                  index={position++ % ANIMATION_PAGE_SIZE}
                  onClick={
                    item.direction === "sent" ? onOpenReceipt : undefined
                  }
                />
              ))}
            </S.List>
          </S.Group>
        ))}

        <div ref={sentinelRef} />

        {loading && (
          <S.LoadingMore>
            <CircularProgress size={24} color="inherit" />
          </S.LoadingMore>
        )}

        {error && (
          <S.Feedback>
            <S.FeedbackText>Erro ao carregar mais transações.</S.FeedbackText>
            <S.FeedbackButton type="button" onClick={retry}>
              Tentar novamente
            </S.FeedbackButton>
          </S.Feedback>
        )}

        {!hasMore && items.length > 5 && (
          <S.EndText>Você chegou ao fim do extrato</S.EndText>
        )}
      </>
    );
  };

  return (
    <S.Container>
      <StatementFilters
        type={type}
        onTypeChange={setType}
        period={period}
        customRange={customRange}
        onPeriodChange={handlePeriodChange}
        search={search}
        onSearchChange={setSearch}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
      />
      {renderContent()}
    </S.Container>
  );
};

export default Statement;
